import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSidebar } from '../../contexts/SidebarContext';
import { X, Captions, Mic, Copy, Bot, ArrowRight } from 'lucide-react';

const DISMISS_KEY = 'dp-skills-howto-dismissed';

/**
 * Faixa "Como funciona" no topo da Biblioteca: conecta a biblioteca ao fluxo
 * real do DarkPlanner (o SRT nasce na Transcrição ou nas legendas do Gerador
 * de Áudio; as skills entram no meio; o resultado vai para a IA do usuário).
 * Dispensável (X), lembrado por localStorage.
 */
const HowItWorksStrip = () => {
  const { t } = useTranslation();
  const { isMobile } = useSidebar();
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });

  if (dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch {}
  };

  const stepNumber = (n) => (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0,
      background: 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)',
      color: '#FFFFFF', fontSize: '11.5px', fontWeight: 700,
      fontFamily: 'var(--dp-font)',
      boxShadow: '0 3px 10px rgba(13, 174, 255, 0.25)',
    }}>
      {n}
    </span>
  );

  const stepTitleStyle = {
    fontSize: '12.5px', fontWeight: 600, color: 'var(--dp-text-primary)',
    fontFamily: 'var(--dp-font)', lineHeight: 1.35,
  };

  const stepDescStyle = {
    fontSize: '11.5px', color: 'var(--dp-text-secondary)',
    fontFamily: 'var(--dp-font)', lineHeight: 1.5, margin: '2px 0 0',
  };

  const toolLinkStyle = {
    display: 'inline-flex', alignItems: 'center', gap: '4px',
    fontSize: '11px', fontWeight: 600, fontFamily: 'var(--dp-font)',
    color: 'var(--dp-blue-500, #0DAEFF)', textDecoration: 'none',
    padding: '3px 9px', borderRadius: '9999px',
    background: 'rgba(13, 174, 255, 0.08)', border: '1px solid rgba(13, 174, 255, 0.22)',
    transition: 'all 0.2s ease', whiteSpace: 'nowrap',
  };

  return (
    <section className="dp-panel dp-top-accent" style={{ marginBottom: '20px', position: 'relative' }}>
      <button
        type="button"
        title={t('skills.howto.dismiss')}
        onClick={dismiss}
        style={{
          position: 'absolute', top: '10px', right: '10px', zIndex: 2,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: '24px', height: '24px', borderRadius: '7px',
          border: '1px solid var(--dp-border-subtle)', background: 'transparent',
          color: 'var(--dp-text-tertiary)', cursor: 'pointer', transition: 'all 0.2s ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dp-text-primary)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dp-text-tertiary)'; }}
      >
        <X size={13} />
      </button>

      <div style={{
        display: 'flex', flexDirection: isMobile ? 'column' : 'row',
        alignItems: 'stretch', gap: isMobile ? '14px' : '0',
        padding: '16px 18px',
      }}>
        {/* Passo 1: gerar o SRT dentro do DarkPlanner */}
        <div style={{ flex: '1 1 0', minWidth: 0, display: 'flex', gap: '10px', paddingRight: isMobile ? 0 : '18px' }}>
          {stepNumber(1)}
          <div style={{ minWidth: 0 }}>
            <div style={stepTitleStyle}>{t('skills.howto.step1_title')}</div>
            <p style={stepDescStyle}>{t('skills.howto.step1_desc')}</p>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '7px' }}>
              <Link
                to="/transcricao"
                style={toolLinkStyle}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(13, 174, 255, 0.14)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(13, 174, 255, 0.08)'; }}
              >
                <Captions size={11} />
                {t('skills.howto.step1_link_transcricao')}
              </Link>
              <Link
                to="/audio-generator"
                style={toolLinkStyle}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(13, 174, 255, 0.14)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(13, 174, 255, 0.08)'; }}
              >
                <Mic size={11} />
                {t('skills.howto.step1_link_audio')}
              </Link>
            </div>
          </div>
        </div>

        {/* Divisor */}
        <div style={{
          width: isMobile ? '100%' : '1px',
          height: isMobile ? '1px' : 'auto',
          background: 'var(--dp-border-subtle)', flexShrink: 0,
        }} />

        {/* Passo 2: copiar as skills */}
        <div style={{ flex: '1 1 0', minWidth: 0, display: 'flex', gap: '10px', padding: isMobile ? 0 : '0 18px' }}>
          {stepNumber(2)}
          <div style={{ minWidth: 0 }}>
            <div style={{ ...stepTitleStyle, display: 'flex', alignItems: 'center', gap: '5px' }}>
              {t('skills.howto.step2_title')}
              <Copy size={12} style={{ color: 'var(--dp-blue-500, #0DAEFF)', flexShrink: 0 }} />
            </div>
            <p style={stepDescStyle}>{t('skills.howto.step2_desc')}</p>
          </div>
        </div>

        {/* Divisor */}
        <div style={{
          width: isMobile ? '100%' : '1px',
          height: isMobile ? '1px' : 'auto',
          background: 'var(--dp-border-subtle)', flexShrink: 0,
        }} />

        {/* Passo 3: colar na IA */}
        <div style={{ flex: '1 1 0', minWidth: 0, display: 'flex', gap: '10px', paddingLeft: isMobile ? 0 : '18px', paddingRight: isMobile ? 0 : '20px' }}>
          {stepNumber(3)}
          <div style={{ minWidth: 0 }}>
            <div style={{ ...stepTitleStyle, display: 'flex', alignItems: 'center', gap: '5px' }}>
              {t('skills.howto.step3_title')}
              <Bot size={12} style={{ color: 'var(--dp-blue-500, #0DAEFF)', flexShrink: 0 }} />
            </div>
            <p style={stepDescStyle}>{t('skills.howto.step3_desc')}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksStrip;
